import { useCallback, useRef, useState } from 'react'

export interface HeadlinePhase {
  at: number
  text: string
}

export function scheduleHeadlines(
  phases: HeadlinePhase[],
  setActiveHeadline: (text: string) => void,
  swapHeadline: (text: string) => void,
  addTimer: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>,
  loopInterval?: number,
) {
  if (phases.length === 0) return

  const cycleMs = loopInterval ?? phases[phases.length - 1].at + 5000

  const scheduleOneCycle = (offset: number) => {
    if (offset === 0) {
      setActiveHeadline(phases[0].text)
    } else {
      addTimer(() => swapHeadline(phases[0].text), offset)
    }
    for (let i = 1; i < phases.length; i++) {
      addTimer(() => swapHeadline(phases[i].text), offset + phases[i].at)
    }
    addTimer(() => scheduleOneCycle(offset + cycleMs), offset + cycleMs)
  }

  scheduleOneCycle(0)
}

export function useLoaderAnimation(twSpeed: number, twJitter: number) {
  const timerRefs = useRef<ReturnType<typeof setTimeout>[]>([])

  const addTimer = useCallback((fn: () => void, ms: number) => {
    const id = setTimeout(fn, ms)
    timerRefs.current.push(id)
    return id
  }, [])

  const clearTimers = useCallback(() => {
    timerRefs.current.forEach(clearTimeout)
  }, [])

  const typewrite = useCallback(
    (text: string, setter: (s: string) => void, onDone: () => void) => {
      let i = 0
      const tick = () => {
        if (i < text.length) {
          i++
          setter(text.slice(0, i))
          const jitter = Math.random() * twJitter * 2 - twJitter
          addTimer(tick, twSpeed + jitter)
        } else {
          addTimer(onDone, 280)
        }
      }
      tick()
    },
    [addTimer, twSpeed, twJitter],
  )

  const [activeHeadline, setActiveHeadline] = useState('')
  const [exitHeadline, setExitHeadline] = useState<string | null>(null)

  const swapHeadline = useCallback(
    (text: string) => {
      setActiveHeadline((current) => {
        setExitHeadline(current)
        return text
      })
      addTimer(() => setExitHeadline(null), 400)
    },
    [addTimer],
  )

  return { addTimer, clearTimers, typewrite, activeHeadline, setActiveHeadline, exitHeadline, swapHeadline }
}
